import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../data/constants";

const AuthContext = createContext(null);
const TOKEN_KEY = "funasia_admin_token";

const buildHeaders = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  // null = not yet checked, true = checking, false = checked
  const [isChecking, setIsChecking] = useState(true);

  const refreshMe = useCallback(async (currentToken) => {
    const t = currentToken ?? token;
    if (!t) {
      setUser(null);
      setIsChecking(false);
      return null;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: buildHeaders(t) });
      setUser(data);
      return data;
    } catch (err) {
      // Token expired or invalid — clear it and force re-login.
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(null);
      return null;
    } finally {
      setIsChecking(false);
    }
  }, [token]);

  useEffect(() => {
    refreshMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  const authedAxios = useCallback(
    (config = {}) => axios({ ...config, headers: { ...(config.headers || {}), ...buildHeaders(token) } }),
    [token]
  );

  return (
    <AuthContext.Provider value={{ user, token, isChecking, login, logout, refreshMe, authedAxios }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
